// PSD writer — inverse of `parsePsd`.
//
// Scope of this slice (M6 round-trip half):
//   - PSD v1 and PSB (version 2).  All length fields widen to u64 for PSB.
//   - RGB color mode only.  Channel-order mapping for CMYK / Lab / Indexed
//     belongs in a per-mode helper added when those modes are wired up.
//   - 8-bit depth only for the input pixel buffers (RGBA8).  The high-bit-
//     depth pipeline is M9; the writer must not be fed downsampled 8-bit
//     when 16/32-bit fidelity is wanted.
//   - PackBits (RLE) compression for every channel.  ZIP / ZIP-prediction
//     encoders are not implemented (see `encoders.ts` for rationale).
//
// Round-trip strategy:
//   - Image resources pass through byte-for-byte from `file.imageResources`.
//   - Each layer's `maskData`, `blendingRangesData`, and `additionalInfo`
//     blocks pass through verbatim.  This means writer output for an
//     unmodified `PsdFile` differs from the original input only in:
//       (a) channel pixel data (re-encoded with our PackBits)
//       (b) merged-composite pixel data (re-encoded with our PackBits)
//       (c) section padding (we emit the same alignment the reader expects)
//
// Backpatching:
//   - Section 4 (layer & mask info) total length: deferred u32.
//   - Layer info sub-section length: deferred u32.
//   - Per-layer extra-data length: deferred u32 (covers mask + blend ranges
//     + Pascal name + additional info blocks).
//   - Per-channel data length inside each layer record: deferred u32 each.
//     Critical: the layer record declares `channels[i].length` before the
//     channel data itself is written, so we have to back-patch each one
//     once the channel's encoded byte count is known.
//
// Layout reference: ./README.md "File-layout cheatsheet" and the Adobe
// Photoshop File Formats Specification.

import { BinaryWriter } from "./BinaryWriter";
import { encodePackBits } from "./encoders";
import type {
  PsdAdditionalLayerInfo,
  PsdFile,
  PsdLayerRecord,
  PsdResource,
} from "./types";

export class PsdWriteError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PsdWriteError";
  }
}

const SIGNATURE = "8BPS";
const RESOURCE_SIGNATURE = "8BIM";
const COMP_RLE = 1;

/**
 * Encode a `PsdFile` plus the per-layer pixel buffers and a merged composite
 * back into a PSD byte stream.  The output is structurally a valid PSD v1
 * that `parsePsd` will re-read with matching header / layer rect / channel
 * IDs / etc.
 *
 * Pixel input contract:
 *   - `layerPixels` maps each layer's index (in `file.layerInfo.layers`) to
 *     a tightly-packed RGBA8 buffer of size `layerWidth * layerHeight * 4`
 *     using the layer's rect (right-left × bottom-top).
 *   - Layers with zero area (top==bottom or left==right) need no entry; the
 *     writer emits empty channel blocks for them.
 *   - `mergedComposite` is RGBA8 sized to `file.header.width * file.header.
 *     height * 4`.  It is written verbatim as the document-level section 5.
 */
export function writePsd(
  file: PsdFile,
  layerPixels: ReadonlyMap<number, Uint8Array>,
  mergedComposite: Uint8Array,
): Uint8Array {
  if (file.header.depth !== 8) {
    throw new PsdWriteError(
      `only 8-bit depth is supported by the writer (got depth ${file.header.depth})`,
    );
  }
  if (file.header.colorMode !== 3) {
    throw new PsdWriteError(
      `only RGB color mode (3) is supported by the writer ` +
        `(got colorMode ${file.header.colorMode})`,
    );
  }

  const w = new BinaryWriter(16 * 1024);

  writeHeader(w, file);
  writeColorModeData(w, file);
  writeImageResources(w, file.imageResources, file.header.version === 2);
  writeLayerAndMaskInfo(w, file, layerPixels);
  writeMergedImageData(w, file, mergedComposite);

  return w.toUint8Array();
}

// -- Section 1: File header ----------------------------------------------

function writeHeader(w: BinaryWriter, file: PsdFile): void {
  const { header } = file;
  // Signature
  for (let i = 0; i < SIGNATURE.length; i++) {
    w.writeU8(SIGNATURE.charCodeAt(i));
  }
  w.writeU16(header.version);
  // 6 reserved zero bytes
  for (let i = 0; i < 6; i++) w.writeU8(0);
  w.writeU16(header.channels);
  w.writeU32(header.height);
  w.writeU32(header.width);
  w.writeU16(header.depth);
  w.writeU16(header.colorMode);
}

// -- Section 2: Color mode data ------------------------------------------

function writeColorModeData(w: BinaryWriter, file: PsdFile): void {
  const largeDoc = file.header.version === 2;
  if (largeDoc) {
    w.writeU64(file.colorModeData.byteLength);
  } else {
    w.writeU32(file.colorModeData.byteLength);
  }
  if (file.colorModeData.byteLength > 0) {
    w.writeBytes(file.colorModeData);
  }
}

// -- Section 3: Image resources ------------------------------------------

function writeImageResources(
  w: BinaryWriter,
  resources: readonly PsdResource[],
  largeDoc: boolean,
): void {
  const slot = largeDoc ? w.reserveU64() : w.reserveU32();
  const start = w.length();
  for (const res of resources) {
    // 4-byte signature "8BIM"
    for (let i = 0; i < RESOURCE_SIGNATURE.length; i++) {
      w.writeU8(RESOURCE_SIGNATURE.charCodeAt(i));
    }
    w.writeU16(res.id);
    // Resource name: Pascal string, padded to multiple of 2.
    w.writePascalString(res.name, 2);
    // Resource data, length-prefixed; data itself padded to even.
    w.writeU32(res.data.byteLength);
    w.writeBytes(res.data);
    if (res.data.byteLength % 2 === 1) w.writeU8(0);
  }
  slot.commit(w.length() - start);
}

// -- Section 4: Layer & mask info ----------------------------------------

function writeLayerAndMaskInfo(
  w: BinaryWriter,
  file: PsdFile,
  layerPixels: ReadonlyMap<number, Uint8Array>,
): void {
  const largeDoc = file.header.version === 2;
  const sectionSlot = largeDoc ? w.reserveU64() : w.reserveU32();
  const sectionStart = w.length();

  const layers = file.layerInfo.layers;
  if (layers.length === 0 && file.layerInfo.globalLayerMaskData.byteLength === 0
      && file.layerInfo.additionalInfo.length === 0) {
    // Empty layer & mask info section is allowed for flat-composite-only
    // files.  Leave the section length at zero.
    sectionSlot.commit(0);
    return;
  }

  // Layer info sub-section.
  const layerInfoSlot = largeDoc ? w.reserveU64() : w.reserveU32();
  const layerInfoStart = w.length();

  // Layer count (signed; preserve the parsed sign for round-trip).
  const layerCountRaw =
    file.layerInfo.layerCountRaw !== 0
      ? file.layerInfo.layerCountRaw
      : layers.length;
  w.writeI16(layerCountRaw);

  // Pre-encode every channel's image bytes so we know their final size
  // before writing the layer records (which declare each channel's length).
  // Each entry: encoded channel bytes (including the 2-byte compression
  // code + RLE scanline table, ready to drop into the channel-image area).
  const perLayerEncoded: Uint8Array[][] = [];
  for (let li = 0; li < layers.length; li++) {
    const layer = layers[li]!;
    const width = layer.right - layer.left;
    const height = layer.bottom - layer.top;
    const rgba = layerPixels.get(li);
    const encoded = encodeLayerChannels(layer, width, height, rgba, largeDoc);
    perLayerEncoded.push(encoded);
  }

  // -- Write the layer records (each declares its per-channel lengths) --
  // We back-patch each channel length once we know the encoded byte count.
  const channelLengthSlots: { commit: (n: number) => void }[][] = [];
  for (let li = 0; li < layers.length; li++) {
    const layer = layers[li]!;
    const slots = writeLayerRecord(w, layer, largeDoc);
    channelLengthSlots.push(slots);
  }

  // -- Write the channel image data, back-patching lengths as we go ----
  for (let li = 0; li < layers.length; li++) {
    const encodedChannels = perLayerEncoded[li]!;
    const slots = channelLengthSlots[li]!;
    if (encodedChannels.length !== slots.length) {
      throw new PsdWriteError(
        `internal: channel length slot count (${slots.length}) does not ` +
          `match encoded channel count (${encodedChannels.length}) for layer ${li}`,
      );
    }
    for (let ci = 0; ci < encodedChannels.length; ci++) {
      const blob = encodedChannels[ci]!;
      w.writeBytes(blob);
      slots[ci]!.commit(blob.byteLength);
    }
  }

  // Layer info sub-section is padded to a multiple of 2.
  w.pad(2);
  layerInfoSlot.commit(w.length() - layerInfoStart);

  // Global Layer Mask Info sub-section: pass through.
  w.writeU32(file.layerInfo.globalLayerMaskData.byteLength);
  if (file.layerInfo.globalLayerMaskData.byteLength > 0) {
    w.writeBytes(file.layerInfo.globalLayerMaskData);
  }

  // Document-level additional info blocks.
  for (const block of file.layerInfo.additionalInfo) {
    writeAdditionalInfoBlock(w, block, largeDoc);
  }

  sectionSlot.commit(w.length() - sectionStart);
}

function writeLayerRecord(
  w: BinaryWriter,
  layer: PsdLayerRecord,
  largeDoc: boolean,
): { commit: (n: number) => void }[] {
  w.writeI32(layer.top);
  w.writeI32(layer.left);
  w.writeI32(layer.bottom);
  w.writeI32(layer.right);
  w.writeU16(layer.channels.length);

  // Channel descriptors: id (i16) + length (u32, deferred).
  const channelLengthSlots: { commit: (n: number) => void }[] = [];
  for (const ch of layer.channels) {
    w.writeI16(ch.id);
    channelLengthSlots.push(largeDoc ? w.reserveU64() : w.reserveU32());
  }

  // "8BIM" + blend mode key (4 ASCII).
  for (let i = 0; i < 4; i++) w.writeU8("8BIM".charCodeAt(i));
  const key = layer.blendModeKey.padEnd(4, " ").slice(0, 4);
  for (let i = 0; i < 4; i++) w.writeU8(key.charCodeAt(i));

  w.writeU8(layer.opacity);
  w.writeU8(layer.clipping);
  w.writeU8(layer.flags);
  w.writeU8(0); // filler byte

  // Extra data: deferred length covering mask + blending ranges + name +
  // additional info blocks.
  const extraSlot = w.reserveU32();
  const extraStart = w.length();

  // Mask data: length-prefixed.
  w.writeU32(layer.maskData.byteLength);
  if (layer.maskData.byteLength > 0) {
    w.writeBytes(layer.maskData);
  }

  // Blending ranges: length-prefixed.
  w.writeU32(layer.blendingRangesData.byteLength);
  if (layer.blendingRangesData.byteLength > 0) {
    w.writeBytes(layer.blendingRangesData);
  }

  // Pascal name padded to 4.
  w.writePascalString(layer.name, 4);

  // Additional layer info blocks (pass through).
  for (const block of layer.additionalInfo) {
    writeAdditionalInfoBlock(w, block, largeDoc);
  }

  extraSlot.commit(w.length() - extraStart);

  return channelLengthSlots;
}

const ADDITIONAL_KEYS_64BIT = new Set([
  "LMsk",
  "Lr16",
  "Lr32",
  "Layr",
  "Mt16",
  "Mt32",
  "Mtrn",
  "Alph",
  "FMsk",
  "lnk2",
  "FEid",
  "FXid",
  "PxSD",
]);

function writeAdditionalInfoBlock(
  w: BinaryWriter,
  block: PsdAdditionalLayerInfo,
  largeDoc: boolean,
): void {
  // Signature: "8BIM" or "8B64".
  const sig = block.signature.length === 4 ? block.signature : "8BIM";
  for (let i = 0; i < 4; i++) w.writeU8(sig.charCodeAt(i));
  // Key: 4 ASCII chars.
  const key = block.key.padEnd(4, " ").slice(0, 4);
  for (let i = 0; i < 4; i++) w.writeU8(key.charCodeAt(i));
  // Length width matches the parser: 8 bytes when sig=8B64 or (PSB AND key
  // in the wide-length set).
  const wide = sig === "8B64" || (largeDoc && ADDITIONAL_KEYS_64BIT.has(key));
  if (wide) {
    w.writeU64(block.data.byteLength);
  } else {
    w.writeU32(block.data.byteLength);
  }
  if (block.data.byteLength > 0) {
    w.writeBytes(block.data);
  }
  // Pad block payload to even (matches reader: pad to 2).
  if (block.data.byteLength % 2 === 1) w.writeU8(0);
}

// -- Channel encoding helpers --------------------------------------------

/**
 * Build the per-channel encoded blobs for a single layer.  Each blob is
 * "ready to drop in" — it starts with the 2-byte compression code and
 * includes the RLE scanline-count table.
 *
 * For RGB / alpha channels we draw bytes from the supplied RGBA8 buffer;
 * mask channels (ids -2 / -3) are not part of the layer's color image and
 * we don't have a separate mask buffer at this stage, so we emit an empty
 * channel block (compression code + zero-length scanline table for a
 * zero-height mask).  The reader's `decodeLayer` already skips these IDs.
 */
function encodeLayerChannels(
  layer: PsdLayerRecord,
  width: number,
  height: number,
  rgba: Uint8Array | undefined,
  psb: boolean,
): Uint8Array[] {
  const out: Uint8Array[] = [];
  for (const ch of layer.channels) {
    if (ch.id === -2 || ch.id === -3) {
      // Mask channel — emit a minimal empty plane.  Compression code only;
      // no scanlines, no data.  Round-trip is fine because the layer
      // declares the byte count from our `slot.commit(blob.byteLength)`.
      const empty = new Uint8Array(2);
      // U16 BE 0 (raw compression, zero bytes payload).
      out.push(empty);
      continue;
    }
    if (width <= 0 || height <= 0) {
      // Zero-area layer: emit a 2-byte compression code with no scanlines.
      out.push(new Uint8Array([0, 0]));
      continue;
    }
    if (!rgba) {
      throw new PsdWriteError(
        `layer "${layer.name}" requires pixel data (RGBA8) for channel id ${ch.id}`,
      );
    }
    if (rgba.byteLength < width * height * 4) {
      throw new PsdWriteError(
        `layer "${layer.name}" pixel buffer too small ` +
          `(${rgba.byteLength} bytes, need ${width * height * 4})`,
      );
    }
    const plane = extractPlane(rgba, width, height, ch.id);
    out.push(encodeChannelPlaneRle(plane, width, height, psb));
  }
  return out;
}

/**
 * Pull one channel plane out of a tightly-packed RGBA8 buffer.  IDs follow
 * the same RGB convention used by `decodeChannels.assembleRgba`:
 *
 *   0 → R
 *   1 → G
 *   2 → B
 *   3 / -1 → A
 */
function extractPlane(
  rgba: Uint8Array,
  width: number,
  height: number,
  channelId: number,
): Uint8Array {
  let offset: 0 | 1 | 2 | 3;
  switch (channelId) {
    case 0:
      offset = 0;
      break;
    case 1:
      offset = 1;
      break;
    case 2:
      offset = 2;
      break;
    case 3:
    case -1:
      offset = 3;
      break;
    default:
      throw new PsdWriteError(
        `unsupported channel id ${channelId} for RGB write (expected 0/1/2/3/-1)`,
      );
  }
  const pixelCount = width * height;
  const plane = new Uint8Array(pixelCount);
  for (let i = 0; i < pixelCount; i++) {
    plane[i] = rgba[i * 4 + offset]!;
  }
  return plane;
}

/**
 * Encode a single channel plane (one byte per sample, row-major) as PSD
 * RLE: u16 compression(1) + per-row u16 byte counts + concatenated row
 * payloads (each row independently PackBits-encoded).
 */
function encodeChannelPlaneRle(
  plane: Uint8Array,
  width: number,
  height: number,
  psb: boolean,
): Uint8Array {
  const rowEncoded: Uint8Array[] = new Array<Uint8Array>(height);
  let totalRowBytes = 0;
  const maxRowLength = psb ? 0xffff_ffff : 0xffff;
  for (let y = 0; y < height; y++) {
    const row = plane.subarray(y * width, (y + 1) * width);
    const enc = encodePackBits(row);
    if (enc.byteLength > maxRowLength) {
      throw new PsdWriteError(
        `encoded scanline length ${enc.byteLength} exceeds ${psb ? "u32" : "u16"} range`,
      );
    }
    rowEncoded[y] = enc;
    totalRowBytes += enc.byteLength;
  }
  const rowSize = psb ? 4 : 2;
  const headerBytes = 2 + height * rowSize; // u16 compression + height × row counts
  const out = new Uint8Array(headerBytes + totalRowBytes);
  const view = new DataView(out.buffer);
  view.setUint16(0, COMP_RLE, false);
  let off = 2;
  for (let y = 0; y < height; y++) {
    if (psb) {
      view.setUint32(off, rowEncoded[y]!.byteLength, false);
    } else {
      view.setUint16(off, rowEncoded[y]!.byteLength, false);
    }
    off += rowSize;
  }
  for (let y = 0; y < height; y++) {
    out.set(rowEncoded[y]!, off);
    off += rowEncoded[y]!.byteLength;
  }
  return out;
}

// -- Section 5: Merged image data ----------------------------------------

function writeMergedImageData(
  w: BinaryWriter,
  file: PsdFile,
  mergedRgba: Uint8Array,
): void {
  const { width, height, channels: channelCount } = file.header;
  const expected = width * height * 4;
  const psb = file.header.version === 2;
  const maxRowLength = psb ? 0xffff_ffff : 0xffff;
  if (mergedRgba.byteLength < expected) {
    throw new PsdWriteError(
      `merged composite is too small: ${mergedRgba.byteLength} bytes, ` +
        `need ${expected}`,
    );
  }
  // Build channel planes in the document's channel order (R, G, B, A, then
  // any extra channels copied as zero — we don't model spot channels yet).
  // Layout follows the merged-composite spec: ONE compression code, then a
  // single scanline-count table covering channelCount × height rows, then
  // channel-sequential payloads.
  const planeOffsets: (0 | 1 | 2 | 3)[] = [];
  for (let c = 0; c < channelCount; c++) {
    if (c === 0) planeOffsets.push(0);
    else if (c === 1) planeOffsets.push(1);
    else if (c === 2) planeOffsets.push(2);
    else if (c === 3) planeOffsets.push(3);
    else planeOffsets.push(3); // unknown extras → reuse alpha; rare for RGB
  }

  // Pre-encode every row for every channel.
  const rowEncoded: Uint8Array[] = new Array<Uint8Array>(channelCount * height);
  let i = 0;
  for (let c = 0; c < channelCount; c++) {
    const offset = planeOffsets[c]!;
    for (let y = 0; y < height; y++) {
      const row = new Uint8Array(width);
      for (let x = 0; x < width; x++) {
        row[x] = mergedRgba[(y * width + x) * 4 + offset]!;
      }
      const enc = encodePackBits(row);
      if (enc.byteLength > maxRowLength) {
        throw new PsdWriteError(
          `merged composite row ${y} (channel ${c}) encoded to ` +
            `${enc.byteLength} bytes, exceeds ${psb ? "u32" : "u16"} range`,
        );
      }
      rowEncoded[i++] = enc;
    }
  }

  // Write: compression code, full scanline table, concatenated rows.
  w.writeU16(COMP_RLE);
  for (let k = 0; k < rowEncoded.length; k++) {
    if (psb) {
      w.writeU32(rowEncoded[k]!.byteLength);
    } else {
      w.writeU16(rowEncoded[k]!.byteLength);
    }
  }
  for (let k = 0; k < rowEncoded.length; k++) {
    w.writeBytes(rowEncoded[k]!);
  }
}
